import { useState, useEffect, useCallback } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import AiAccessModal from '@/components/common/AiAccessModal'
import { useThemeStore } from '@/store/themeStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { useProjectStore } from '@/store/projectStore'
import { clearAppEnv, setAppEnv } from '@/api/appEnv'
import { installErrorRing } from '@twasta/page-runtime/support/errorRing'
import { trackPageView } from '@twasta/page-runtime/activity'
import { startActivity } from '@/activity/hostTracker'

const MOBILE_BREAKPOINT = 768

// Routes that render their own toolbar/header inside the main slot and
// shouldn't get the outer 1.5rem padding wrapping them. Each of these
// already places a full-bleed bar at the top of its render — adding
// padding around it leaves a visible gap between the app TopBar and
// the editor's own bar.
const FULL_BLEED_PREFIXES = [
  '/designers/',
  '/srceditor/',
]
// Routes that should have ZERO padding (edge-to-edge), not just no top padding.
// Builder Mode's split-pane workspace fills the entire main area.
const ZERO_PADDING_PREFIXES = [
  '/builder/',
]
function isFullBleedRoute(pathname: string): boolean {
  return FULL_BLEED_PREFIXES.some(p => pathname.startsWith(p))
}
function isZeroPaddingRoute(pathname: string): boolean {
  return ZERO_PADDING_PREFIXES.some(p => pathname.startsWith(p))
}


/** A builder route as a readable screen for the activity log:
 *  `/designers/transaction/shop/SALES_ORDER` → "Transaction designer: SALES_ORDER". */
function builderScreen(pathname: string): { name: string; kind: string } {
  const seg = pathname.split('/').filter(Boolean).map((p) => {
    try { return decodeURIComponent(p) } catch { return p }
  })
  const title = (s: string) => s.replace(/[-_]+/g, ' ').replace(/^./, (c) => c.toUpperCase())
  if (seg[0] === 'designers' && seg[1]) {
    const kind = `${title(seg[1])} designer`
    return { name: seg[3] ? `${kind}: ${seg[3]}` : kind, kind: seg[1] }
  }
  if (!seg.length) return { name: 'Home', kind: 'home' }
  const tab = seg[0] === 'workbench' && seg[2] ? ` · ${title(seg[2])}` : ''
  return { name: `${title(seg[0])}${tab}`, kind: seg[0] }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return isMobile
}

export default function AppLayout() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const { theme } = useThemeStore()

  // The left rail (Sidebar) now hosts the Ask-AI agent. Its open/minimised/
  // maximized state is shared via useDockUiStore so the rail and the dock agree.
  // On mobile the same `panelOpen` flag doubles as the overlay-drawer toggle.
  const { panelOpen, maximized, setPanelOpen, togglePanel } = useDockUiStore()
  const railMaximized = maximized && panelOpen

  // Name the project being built on every request (`api/appEnv`): for an
  // application under release management the backend then binds builders to
  // its working copy + development database, consistently on every endpoint.
  const currentFolder = useProjectStore((s) => s.currentProject?.application.folder_name || '')
  const currentOwner = useProjectStore((s) => s.projectAccess?.owner_user_id || '')
  useEffect(() => {
    if (!currentFolder) { clearAppEnv('workbench'); return undefined }
    setAppEnv({ project: currentFolder, owner: currentOwner || undefined, source: 'workbench' })
    return () => clearAppEnv('workbench')
  }, [currentFolder, currentOwner])

  // Failed requests and script errors feed the Support module's report
  // context (a builder files issues from the workbench too).
  useEffect(() => { installErrorRing() }, [])

  // Activity analytics: what Twasta users open in the builder and for how
  // long (their changes are recorded server-side, per API call). The project
  // is the one being built; the server checks it against their access.
  useEffect(() => {
    startActivity({ realm: 'builder', surface: 'builder', owner: currentOwner, project: currentFolder })
  }, [currentFolder, currentOwner])
  useEffect(() => {
    const screen = builderScreen(location.pathname)
    // The screen's own name as its title — the tab title is the product name
    // on every builder screen and says nothing.
    trackPageView(screen.name, { obj_type: screen.kind, path: location.pathname, title: screen.name })
  }, [location.pathname, currentFolder])

  // Apply theme on mount
  useEffect(() => {
    document.body.className = theme === 'light' ? 'light-theme' : ''
  }, [theme])

  // On mobile, close the drawer on route change.
  useEffect(() => {
    if (isMobile) setPanelOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isMobile])

  const toggleSidebar = useCallback(() => {
    togglePanel()
  }, [togglePanel])

  // Allow other components (e.g., NoAppDialog) to request the rail opens.
  useEffect(() => {
    const handler = () => setPanelOpen(true)
    window.addEventListener('twasta:sidebar-open', handler)
    return () => window.removeEventListener('twasta:sidebar-open', handler)
  }, [setPanelOpen])

  const drawerOpen = isMobile && panelOpen

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--color-bg)',
      }}
    >
      {/* Mobile backdrop */}
      {drawerOpen && (
        <div className="sidebar-backdrop" onClick={() => setPanelOpen(false)} />
      )}

      {/* Sidebar (left rail): overlay drawer on mobile, inline on desktop.
          When the agent rail is maximized the main column is hidden and this
          wrapper must grow to fill the row AND become a flex container, so the
          aside's `flex:1` actually expands it to the full viewport. Without
          this the aside (a block child of a plain block wrapper) only sizes to
          its content and maximize wouldn't go full-screen. */}
      <div
        className={isMobile ? (panelOpen ? 'sidebar-mobile' : 'sidebar-mobile-hidden') : ''}
        style={!isMobile && railMaximized ? { flex: 1, minWidth: 0, display: 'flex' } : undefined}
      >
        <Sidebar mobile={isMobile} onNavigate={() => isMobile && setPanelOpen(false)} />
      </div>

      {/* Main column. Hidden while the agent rail is maximized so the panel can
          take the full viewport (Escape / Restore brings it back). */}
      <div
        style={{
          flex: 1,
          display: railMaximized ? 'none' : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <TopBar onToggleSidebar={toggleSidebar} />
        <main
          className="main-shell"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <div
            className="main-content"
            style={{
              flex: 1,
              overflow: 'auto',
              padding: isZeroPaddingRoute(location.pathname)
                ? 0
                : isFullBleedRoute(location.pathname)
                  ? '0 1.5rem 1.5rem'
                  : '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Outlet />
          </div>
        </main>
      </div>

      {/* Global graceful handler for AI access (402) errors. */}
      <AiAccessModal />
    </div>
  )
}
